import React from 'react';
import { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { getUsersDropdown } from '../../services/userService';

const ALLOWED_ROLES: readonly string[] = ['Shift Supervisor', 'Shift Assistant', 'Shift Trainee'];

interface Personnel {
  id: number;
  name: string;
  role: string;
}

interface PersonnelManagementProps {
  personnel: Personnel[];
  setPersonnel: React.Dispatch<React.SetStateAction<Personnel[]>>;
  personnelError?: string;
}

const PersonnelManagement = ({ personnel, setPersonnel, personnelError }: PersonnelManagementProps) => {
  const [users, setUsers] = useState<Personnel[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');

  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const filteredUsers = users.filter((user) => user.name.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getUsersDropdown();

        const allUsers = response?.data || [];

        // Strictly allow only operational shift roles
        const operationalUsers = allUsers
          .filter((user: any) => ALLOWED_ROLES.includes(user.user_type?.title))
          .map((user: any) => ({
            id: user.id,
            name: user.name,
            role: user.user_type?.title || '',
          }));

        setUsers(operationalUsers);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      }
    };

    fetchUsers();
  }, []);

  const handleAdd = () => {
    if (!selectedUserId) return;

    const user = users.find((u) => u.id === Number(selectedUserId));

    if (user && !personnel.find((p) => p.id === user.id)) {
      setPersonnel((prev) => [...prev, user]);

      setSearch('');
      setSelectedUserId('');
    }
  };

  return (
    <div className="py-2">
      <h3 className="text-base font-semibold text-[#13294B] mb-4">Personnel Management</h3>

      {/* Add Personnel */}
      <div className="flex gap-3 mb-6">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Search personnel..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSelectedUserId('');
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
            className="w-full h-12 border border-gray-300 rounded-lg px-4 text-sm outline-none"
          />

          {showDropdown && filteredUsers.length > 0 && (
            <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 max-h-48 overflow-y-auto shadow-md">
              {filteredUsers.map((user) => (
                <div
                  key={user.id}
                  onMouseDown={() => {
                    setSelectedUserId(String(user.id));
                    setSearch(user.name);
                    setShowDropdown(false);
                  }}
                  className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${selectedUserId === String(user.id) ? 'bg-gray-100' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{user.name}</span>

                    <span className="text-xs text-gray-500">({user.role})</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {personnelError && <p className="text-red-500 text-xs mt-1">{personnelError}</p>}
        </div>

        <button onClick={handleAdd} className="px-6 border border-[#415A77] rounded-lg text-[#415A77] font-medium">
          Add
        </button>
      </div>

      {/* Table */}
      <div className="border border-gray-300 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-300">
              <th className="w-20 py-4 border-r border-gray-300 text-center">Sl. No.</th>

              <th className="text-left px-4 py-4">Shift Attendants & Assistants</th>

              <th className="w-20"></th>
            </tr>
          </thead>

          <tbody>
            {personnel.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center py-6 text-gray-400">
                  No personnel added yet.
                </td>
              </tr>
            ) : (
              personnel.map((item, index) => (
                <tr key={item.id} className="border-b border-gray-300 h-16">
                  <td className="text-center border-r border-gray-300 text-gray-500">{index + 1}</td>

                  <td className="px-4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{item.name}</span>

                      <span className="text-xs text-gray-500">({item.role})</span>
                    </div>
                  </td>

                  <td className="text-center">
                    <button
                      onClick={() => setPersonnel((prev) => prev.filter((p) => p.id !== item.id))}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PersonnelManagement;
